import { createContextualCan } from '@casl/react'
import { Ability } from '@casl/ability'
import { computed, IReactionDisposer, observable, when } from 'mobx'
import { Singleton } from 'typescript-ioc/es5'
import { AbilityAction } from '../enums/ability-action.enum'
import { SubjectEntity } from '../enums/ability-entity.enum'
import { AppState } from './app.state'
import AccessControlService from '../services/access-control.service'
import { DomainDto } from '../dtos/domain.dto'
import { AccountDto } from '../dtos/account.dto'
import { UserDto } from '../dtos/user.dto'
import { createContext } from 'react'

const ABILITY_REFRESH_INTERVAL = 60000

const emptyAbilitySet: AppAbility = new Ability()

export interface ICaslSubject<T extends SubjectEntity> {
    __caslSubjectType__: T | string
}
export type AppAbility = Ability<[AbilityAction, any]>

type KnownIdentity = DomainDto | AccountDto | UserDto
export const asCaslSubject = <T extends SubjectEntity>(type: T, conditions: any): ICaslSubject<T> =>
    ({
        __caslSubjectType__: type,
        ...conditions,
    }) as any

/**
 * The demo attributes a domain-owned policy can condition on. The server evaluates them from the
 * domain and its organization, and a condition on an attribute the subject lacks never matches.
 * `isDemo` and `accountIsDemo` are grant conditions on true, so only an explicit true counts: a
 * missing organization flag withholds the grant instead of widening it. `isDemoTemplate` is a grant
 * condition on false (demo content) and on true (template content), so it is set only when the flag
 * is a boolean and left off otherwise: a missing flag must match neither.
 */
export const domainDemoAttributes = (domain?: Partial<DomainDto> | null) => ({
    isDemo: domain?.is_demo === true,
    accountIsDemo: domain?.account_is_demo === true,
    isDemoTemplate: typeof domain?.is_demo_template === 'boolean' ? domain.is_demo_template : undefined,
})

/**
 * Subject for a check against a domain, built from the snake_case DomainDto the API returns.
 */
export const asDomainSubject = (domain?: Partial<DomainDto> | null): ICaslSubject<SubjectEntity.DOMAIN> =>
    asCaslSubject(SubjectEntity.DOMAIN, {
        ...domain,
        statusId: domain?.status_id,
        ...domainDemoAttributes(domain),
    })

/**
 * Subject for a check against content owned by a domain (feeds and the like).
 */
export const asDomainOwnedSubject = <T extends SubjectEntity>(
    type: T,
    domain?: Partial<DomainDto> | null,
): ICaslSubject<T> =>
    asCaslSubject<T>(type, {
        domainId: domain?.id,
        ...domainDemoAttributes(domain),
    })

@Singleton
export class AbilityStore {
    public acs: AccessControlService

    protected abilitiesLoader: Promise<void> | undefined
    protected abilitiesLoaded: boolean = false
    protected autoRefreshHandler: any

    @observable
    protected _abilities: AppAbility = emptyAbilitySet

    protected disposers: {
        load?: IReactionDisposer
        flush?: IReactionDisposer
    } = {}

    public constructor(protected readonly appState: AppState) {
        this.setupEventWatchers()
        this.loadAbilitiesForCurrentUser()
    }

    @computed
    public get abilities() {
        return this._abilities
    }

    /**
     * generates basic AbilityIdentity for the currently active domain's org
     */
    public get currentOrgIdentity(): ICaslSubject<SubjectEntity.ORG> {
        return asCaslSubject(SubjectEntity.ORG, {
            id: this.appState.currentDomain!.accountId,
            statusId: this.appState.currentDomain!.statusId,
        })
    }

    /**
     * generates basic AbilityIdentity for the currently active domain
     */
    public get currentDomainIdentity(): ICaslSubject<SubjectEntity.DOMAIN> {
        return asCaslSubject(SubjectEntity.DOMAIN, {
            id: this.appState.currentDomain?.id,
            statusId: this.appState.currentDomain?.statusId,
        })
    }

    /**
     * generates basic AbilityIdentity for the currently active user
     */
    public get currentUserIdentity(): ICaslSubject<SubjectEntity.USER> {
        return asCaslSubject(SubjectEntity.USER, {
            id: this.appState.currentUser!.id,
        })
    }

    /**
     * passthrough to @casl/ability can(...)
     */
    public can<T extends SubjectEntity>(
        action: AbilityAction,
        subject: T | ICaslSubject<T> | KnownIdentity,
        field?: string,
    ): boolean {
        return this._abilities.can(action, subject, field)
    }

    /**
     * passthrough to @casl/ability cannot(...)
     */
    public cannot<T extends SubjectEntity>(
        action: AbilityAction,
        subject: T | ICaslSubject<T> | KnownIdentity,
        field?: string,
    ): boolean {
        return this._abilities.cannot(action, subject, field)
    }

    /**
     * generates basic AbilityIdentity for given entity type
     * using the currently active domain's org id
     */
    public getOrgOwnedIdentityFor<T extends SubjectEntity>(type: T) {
        return asCaslSubject<T>(type, { accountId: this.appState.currentDomain!.accountId })
    }

    /**
     * generates basic AbilityIdentity for given entity type
     * using the currently active domain's id
     */
    public getDomainOwnedIdentityFor<T extends SubjectEntity>(type: T) {
        return asCaslSubject<T>(type, { domainId: this.appState.currentDomain!.id })
    }

    /**
     * force refresh abilities from api layer
     */
    public async refresh() {
        this._abilities = await this.acs.getAbilities(true)

        try {
            await this.acs.getUserRecords(true)
        } catch (err) {
            if (process.env.IS_LOCAL) {
                console.debug('ability_store_records_refresh_error', err)
            }
        }
    }

    /**
     * Removes abilities and resets state.
     * should only be used when user is logged out
     * of the application.
     */
    public flush = () => {
        if (this.autoRefreshHandler) {
            clearTimeout(this.autoRefreshHandler)
        }

        if (this.abilitiesLoaded) {
            this.abilitiesLoaded = false
            this._abilities = emptyAbilitySet

            // reset load event watcher
            this.setupEventWatchers()
        }
    }

    /**
     * Waits for current user to be loaded.
     * Once available permissions are requested
     * and loaded into the current scope.
     */
    public loadAbilitiesForCurrentUser = async () => {
        if (this.appState.currentUser && !this.abilitiesLoaded && !this.abilitiesLoader) {
            this.abilitiesLoader = new Promise(async (res) => {
                this.abilitiesLoaded = true

                try {
                    this.appState.loadingCount++
                    this.acs = AccessControlService.forUser(this.appState.currentUser!)
                    this._abilities = await this.acs.getAbilities()

                    try {
                        await this.acs.getUserRecords()
                    } catch (err) {
                        if (process.env.IS_LOCAL) {
                            console.debug('ability_store_records_load_error', err)
                        }
                    }

                    // stop watching for load event
                    this.disposers.load?.()

                    // start auto-refresh
                    this.autoRefreshHandler = setInterval(() => this.refresh(), ABILITY_REFRESH_INTERVAL)
                } catch (err) {
                    this.abilitiesLoaded = false

                    if (process.env.IS_LOCAL) {
                        console.debug('ability_store_error', err)
                    }
                } finally {
                    this.appState.loadingCount--

                    res()

                    this.abilitiesLoader = undefined
                }
            })
        }

        return this.abilitiesLoader
    }

    protected setupEventWatchers() {
        this.disposers.load = when(() => !!this.appState.currentUser?.id, this.loadAbilitiesForCurrentUser)
    }
}

/**
 * bound fn component for @casl/react <Can ... />
 * using the injected abilities
 */

export const AbilityContext = createContext({})
export const CurrentUserCan = createContextualCan(AbilityContext.Consumer)
